// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import { parseRelativeUrl } from '../../common/utils/url';
import { defaultInfiniteProductSearchMixin } from '../utils/infiniteScroll';

const DIRECTION_TOP = 'top';
const DIRECTION_BOTTOM = 'bottom';

/**
 * Object that monitors the state of an infinite scroll instance from  vue-infinite-loading
 * @typedef {Object} InfiniteMonitor
 * @property {Number} lastLoadedPage - page number of last loaded page
 * @property {ProducSearch} lastProductSearch - last loaded page
 * @property {Object} $state - vue-infinite-loading $state object
 * @property {boolean} direction - load direction of infinite instance (DIRECTION_TOP or DIRECTION_BOTTOM)
 */

/**
 * Creates a new object that monitors the state of an infinite scroll instance.
 * @param {String} direction - infinite scroll direction
 * @param {import('../utils/infiniteScroll/infiniteProductSearchMixin').InfiniteProductSearch} initialProductSearch - initial page
 * @returns {Object} - New monitor
 */
const infiniteMonitor = (direction, initialProductSearch) => ({
  lastLoadedPage: initialProductSearch?.pageNumber
    ? initialProductSearch.pageNumber
    : 0,
  lastProductSearch: initialProductSearch,
  $state: false,
  direction,
  /**
   * Sets the monitor state as loaded and saves the page number as lastLoadedPage.
   * @param {InfiniteMonitor} monitor - infinite scroll monitor
   * @param {import('../utils/infiniteScroll/infiniteProductSearchMixin').InfiniteProductSearch} productSearch - Newly loaded page
   * @returns {undefined}
   */
  handleLoadedPage(productSearch) {
    if (this.$state === false) {
      // monitor is not loading. Probably user is navigating through history.
      return;
    }
    this.$state.loaded();
    this.lastProductSearch = productSearch;
    this.lastLoadedPage = productSearch.pageNumber;
    this.$state = false;
  },
  /**
   * Returns true if the given monitor (top or down) has reached its latest page
   * for the current productSearch.
   * - Top monitor's latest: first page
   * - Bottom monitor's latest: last page
   * @returns {boolean} - true if monitor has reached its latest page
   */
  hasReachedTheEnd() {
    if (!this.lastProductSearch) return true;
    return this.direction === DIRECTION_TOP
      ? this.lastProductSearch.isFirstPage()
      : this.lastProductSearch.isLastPage();
  },
  /**
   * Returns the url of the next page for a given monitor
   * @returns {string} - url
   */
  nextUrl() {
    if (this.lastProductSearch && !this.hasReachedTheEnd()) {
      const url =
        this.direction === DIRECTION_TOP
          ? this.lastProductSearch.getPreviousPageUrl()
          : this.lastProductSearch.getNextPageUrl();
      return url;
    }
    return false;
  },
  /**
   * Returns the category of the next page
   * @returns {Object} - Next page category
   */
  nextUrlCategory() {
    if (this.lastProductSearch) {
      return this.direction === DIRECTION_TOP
        ? this.lastProductSearch.category
        : this.lastProductSearch.getNextPageCategory();
    }
    return null;
  },
  /**
   * Returns true if the next page url is from another category
   * @returns {boolean} - will change
   */
  willCategoryChangeInNextPage() {
    const nextCatId =
      this.lastProductSearch?.getNextPageCategory()?.id ?? false;
    return nextCatId && this.lastProductSearch?.category?.id !== nextCatId;
  },
});

/**
 * Creates a clean state properties for data to monitor a new search.
 * @param {import('../utils/infiniteScroll/infiniteProductSearchMixin').InfiniteProductSearch} productSearch - initial page number
 * @returns {Object} - Cleaned properties for data
 */
const resetInfiniteMonitorsToPage = (productSearch) => ({
  top: infiniteMonitor(DIRECTION_TOP, productSearch),
  bottom: infiniteMonitor(DIRECTION_BOTTOM, productSearch),
});

const createInfiniteScrollMixin = (infiniteProductSearchMixin) => ({
  data() {
    return {
      infinite: {
        constants: {
          DIRECTION_TOP,
          DIRECTION_BOTTOM,
        },
        ...resetInfiniteMonitorsToPage(),
      },
    };
  },

  watch: {
    products(products, oldProducts) {
      const newLength = products?.length ? products.length : 0;
      const oldLength = oldProducts?.length ? oldProducts.length : 0;
      if (newLength > 0 && oldLength > 0 && newLength > oldLength) {
        const direction =
          products[0].product?.id !== oldProducts[0].product?.id
            ? DIRECTION_TOP
            : DIRECTION_BOTTOM;
        this.infinite[direction].handleLoadedPage(
          infiniteProductSearchMixin(this.productSearch)
        );
      } else {
        // first load (oldLength === 0)
        // or
        // returning back in history (newLength < oldLength)
        this.infiniteReset(this.productSearch);
      }
    },
    productSearch(newSearch, oldSearch) {
      if (
        newSearch?.searchKeywords &&
        newSearch?.searchKeywords !== oldSearch?.searchKeywords
      ) {
        this.infiniteReset(newSearch);
      } else if (
        newSearch?.isCategorySearch &&
        oldSearch?.isCategorySearch &&
        newSearch?.category?.id !== oldSearch?.category?.id
      ) {
        this.infiniteReset(newSearch);
      }
    },
  },

  created() {
    // first load
    this.infiniteReset(this.productSearch);
  },

  methods: {
    /**
     * Handle the infinite event of vue-infinite-loading
     * @param {Object} $state - vue-infinite-loading $state object
     * @param {Function} fetchNextPageFn - function to call passing the next page url
     * @param {string} direction - load direction of infinite instance (DIRECTION_TOP or DIRECTION_BOTTOM)
     */
    infiniteHandle($state, fetchNextPageFn, direction = DIRECTION_BOTTOM) {
      const monitor = this.infinite[direction];
      if (monitor.hasReachedTheEnd()) {
        $state.complete();
        return;
      }

      if (!monitor.$state) {
        const nextUrl = monitor.nextUrl();
        if (nextUrl) {
          monitor.$state = $state;
          const relativeUrlForVueRouter = parseRelativeUrl(nextUrl);
          fetchNextPageFn(relativeUrlForVueRouter, {
            nextPageCategory: monitor.nextUrlCategory(),
            willCategoryChangeInNextPage:
              monitor.willCategoryChangeInNextPage(),
          });
        } else if ($state) {
          $state.loaded();
        }
      }
    },

    /**
     * Reset the infiniteProductSearchMixin and set the initial page.
     * @param {ProductSearch} initialProductSearch - initial page
     */
    infiniteReset(initialProductSearch) {
      this.infinite = {
        ...this.infinite,
        ...resetInfiniteMonitorsToPage(
          infiniteProductSearchMixin(initialProductSearch)
        ),
      };
    },
  },
});

// eslint-disable-next-line import/prefer-default-export
export const defaultInfiniteScrollMixin = createInfiniteScrollMixin(
  defaultInfiniteProductSearchMixin
);
